import React, { useState } from "react";
import {
  Button,
  Modal,
  Input,
  Space,
  message,
  Popconfirm,
  Tooltip,
  Form,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import axios from "axios";
import RequestHandler from "../../util/RequestHandler";

/**
 * Kullanım:
 * <ApprovalButtons
 *    entityId={entity.id}
 *    entityType="purchaseRequest" // quote | order
 *    status={entity.statusByte}
 *    canApprove={entity.canApprove}
 *    canReject={entity.canReject}
 *    canCancel={entity.canCancel}
 *    isAdmin={entity.isAdmin} // opsiyonel
 *    onSuccess={() => reloadEntity()}
 * />
 */
const ApprovalButtons = ({
  entityId,
  entityType,
  status,
  canApprove = false,
  canReject = false,
  canCancel = false,
  isAdmin = false,
  onSuccess,
  form,
  additionalComments,
  additionalCommentsExtra,
  documentVms,
  files,
  isAdditionalApprovalRequired,
}) => {
  const [loading, setLoading] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [reason, setReason] = useState("");

  // ✅ endpoint mapping
  const endpointMap = {
    purchaseRequest: "/PurchaseRequest/UpdateStatus",
    quote: "/Quote/UpdateStatus",
    order: "/Order/UpdateStatus",
  };

  // ✅ kilitli statüler (onaylanmış, reddedilmiş, tamamlanmış)
  const lockedStatusMap = {
    purchaseRequest: [2, 4, 5],
    quote: [4, 3],
    order: [2, 3],
  };
  const isLocked = lockedStatusMap[entityType]?.includes(status);

  // ✅ API çağrısı
  const sendStatusUpdate = async (payload, successMsg) => {
    await form.validateFields(); // required alanlar dolu mu kontrol et
    setLoading(true);
    try {
      const formData = new FormData();
      payload.documentVms = documentVms || [];
      formData.append("jsonString", JSON.stringify(payload));
      // Files alanı: her dosyayı ayrı ayrı 'Files' olarak append et
      files.forEach((file) => {
        formData.append("files", file.originFileObj);
      });

      var response = await RequestHandler.post(
        endpointMap[entityType],
        formData,
        true,
        {},
        "multipart/form-data"
      );

      if (response?.success) {
        message.success(successMsg);
        onSuccess();
      }
    } catch (err) {
      message.error("İşlem başarısız oldu.");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = () =>
    sendStatusUpdate(
      {
        id: entityId,
        isApproval: true,
        isReject: false,
        reason: null,
        additionalComments: additionalComments,
        additionalCommentsExtra: additionalCommentsExtra,
        isAdditionalApprovalRequired: isAdditionalApprovalRequired ?? false,
      },
      "Kayıt başarıyla onaylandı."
    );

  const handleReject = () => {
    if (!reason.trim()) {
      message.warning("Lütfen reddetme sebebini giriniz.");
      return;
    }
    sendStatusUpdate(
      { id: entityId, isApproval: false, isReject: true, reason },
      "Kayıt reddedildi."
    );
    setRejectModalVisible(false);
    setReason("");
  };

  const handleCancel = () =>
    sendStatusUpdate(
      { id: entityId, isApproval: false, isReject: false, reason: null },
      "Kayıt iptal edildi."
    );

  // ✅ admin override
  const canDo = (permission) => isAdmin || permission;

  return (
    <div
      style={{
        marginTop: 32,
        borderTop: "1px solid #f0f0f0",
        paddingTop: 16,
        textAlign: "right",
      }}
    >
      <Space size="middle">
        {/* ✅ ONAY */}
        <Tooltip
          title={
            !canDo(canApprove) || isLocked
              ? "Bu durumda onay yapılamaz"
              : "Onayla"
          }
        >
          <Popconfirm
            title="Onaylamak istediğinize emin misiniz?"
            onConfirm={handleApprove}
            okText="Evet"
            cancelText="Hayır"
            disabled={!canDo(canApprove) || isLocked}
          >
            <Button
              icon={<CheckCircleOutlined />}
              type="primary"
              disabled={!canDo(canApprove) || isLocked}
              loading={loading}
            >
              Onayla
            </Button>
          </Popconfirm>
        </Tooltip>

        {/* ❌ REDDET */}
        <Tooltip
          title={
            !canDo(canReject) || isLocked ? "Bu durumda reddedilemez" : "Reddet"
          }
        >
          <Button
            icon={<CloseCircleOutlined />}
            danger
            disabled={!canDo(canReject) || isLocked}
            onClick={() => setRejectModalVisible(true)}
          >
            Reddet
          </Button>
        </Tooltip>

        {/* 🛑 İPTAL */}
        <Tooltip
          title={
            !canDo(canCancel) || isLocked
              ? "Bu durumda iptal edilemez"
              : "İptal Et"
          }
        >
          <Popconfirm
            title="Kaydı iptal etmek istediğinize emin misiniz?"
            onConfirm={handleCancel}
            okText="Evet"
            cancelText="Hayır"
            disabled={!canDo(canCancel) || isLocked}
          >
            <Button
              icon={<StopOutlined />}
              disabled={!canDo(canCancel) || isLocked}
              loading={loading}
            >
              İptal Et
            </Button>
          </Popconfirm>
        </Tooltip>
      </Space>

      {/* 🧾 REDDET MODAL */}
      <Modal
        visible={rejectModalVisible}
        title="Reddetme Sebebi"
        okText="Gönder"
        cancelText="Vazgeç"
        onCancel={() => setRejectModalVisible(false)}
        onOk={handleReject}
        confirmLoading={loading}
      >
        <Input.TextArea
          rows={4}
          placeholder="Lütfen reddetme nedenini yazınız..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default ApprovalButtons;
